import React, { useCallback, useEffect, useRef, useState } from "react";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import { Greek } from "flatpickr/dist/l10n/gr.js";
import "./AvailabilityCalendar.css";

// Small helper: one-line horizontal scroll with arrow hints (mobile only)
const ScrollRow = ({ children, className = "", resetKey = 0, ariaLabel = "" }) => {
  const viewportRef = useRef(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const update = useCallback(() => {
    const el = viewportRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    el.scrollLeft = 0;
    requestAnimationFrame(update);
  }, [resetKey, update]);

  useEffect(() => {
    update();
    const onResize = () => update();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [update]);

  return (
    <div className={`ts-scroll-wrap ${canLeft ? "can-left" : ""} ${canRight ? "can-right" : ""}`}>
      <button
        type="button"
        className="ts-scroll-arrow left"
        aria-label="Scroll left"
        onClick={() => viewportRef.current?.scrollBy({ left: -160, behavior: "smooth" })}
      >
        {"\u2039"}
      </button>

      <div
        className={`ts-scroll-viewport ${className}`}
        ref={viewportRef}
        onScroll={update}
        aria-label={ariaLabel}
      >
        {children}
      </div>

      <button
        type="button"
        className="ts-scroll-arrow right"
        aria-label="Scroll right"
        onClick={() => viewportRef.current?.scrollBy({ left: 160, behavior: "smooth" })}
      >
        {"\u203A"}
      </button>
    </div>
  );
};

/**
 * AvailabilityCalendar
 * Extracted from ReservationForm with minimal behavioral changes.
 * Note: It relies on the parent for data fetching + state.
 */
const AvailabilityCalendar = ({
  showAvailabilityModal,
  onClose,

  availabilityDate,
  setAvailabilityDate,
  today,
  parseYmdLocal,

  isGreek,
  tr,

  visibleFilters,
  areaFilters,
  setAreaFilters,
  isMobile,

  calendarInfo,
  calenderStep,
  fetchAvailability,
  loadingAvailability,

  visibleTables,
  visibleTablesCount,
  calculateSlotAvailability,

  reservation,
  reservationDurationSlots,
  reservationBlockWidth,
  hoverBlockWidth,

  containerRef,
  tbodyRef,
  hoverRef,

  setReservationDetails,
  setSelectedTables,
  next5SlotToday,
  isSameLocalDay,
}) => {
  if (!showAvailabilityModal) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal centered"
        style={{
          inset: "0px",
          margin: "auto",
          padding: "8px 10px",
          border: "0",
          borderRadius: 10,
          maxWidth: "88vw",
          height: "fit-content",
          maxHeight: "80vh",
          overflow: "auto",
          width: "835px",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ display: "inline-flex", position: "relative", marginBottom: 0 }}>
          <span style={{ marginRight: "10px" }}>{tr("See tables availability for")}</span>

          {new Date(availabilityDate).toLocaleDateString() !== new Date().toLocaleDateString() && (
            <span
              style={{ cursor: "pointer" }}
              onClick={() => {
                setAvailabilityDate(calenderStep(-1));
                fetchAvailability(calenderStep(-1));
              }}
            >
              {"\u23EA"}
            </span>
          )}

          <Flatpickr
            value={availabilityDate ? parseYmdLocal(availabilityDate) : parseYmdLocal(today)}
            options={{
              dateFormat: "Y-m-d", // keep ISO for backend
              altInput: true, // show a user-friendly version
              altFormat: "l, d/m/Y", // weekday + dd/MM/YYYY
              minDate: "today",
              altInputClass: "custom-flatpickr", // custom style class
              locale: isGreek ? Greek : undefined,
            }}
            onChange={([selectedDate]) => {
              if (!selectedDate) return;

              const year = selectedDate.getFullYear();
              const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
              const day = String(selectedDate.getDate()).padStart(2, "0");
              const ymd = tr("{{year}}-{{month}}-{{day}}", {
                year,
                month,
                day,
              });

              // ? Keep calendar date in sync with the picker (and refresh data)
              setAvailabilityDate(ymd);
              fetchAvailability(ymd);

              // pick time based on whether selected date is today
              const todayD = new Date();
              let nextTime = { h: 12, m: 0, hhmm: "12:00" };
              if (isSameLocalDay(selectedDate, todayD)) {
                nextTime = next5SlotToday();
              }

              setReservationDetails((prev) => ({
                ...prev,
                date: ymd,
                baseHours: nextTime.h,
                baseMinutes: nextTime.m,
                addedHours: 0,
                addedMinutes: 0,
                reservationTime: nextTime.hhmm,
              }));
            }}
            className="date-picker"
          />

          <span
            style={{ cursor: "pointer" }}
            onClick={() => {
              setAvailabilityDate(calenderStep(+1));
              fetchAvailability(calenderStep(+1));
            }}
          >
            {"\u23E9"}
          </span>

          {calendarInfo[availabilityDate] && (
            <table
              style={{
                position: "absolute",
                right: "-175px",
                top: "-20px",
                fontSize: "13px",
                fontWeight: "lighter",
                textAlign: "right",
              }}
            >
              <tbody>
                <tr>
                  <td>{tr("Reservations :")}</td>
                  <th>{calendarInfo[availabilityDate].reservations || 0}</th>
                </tr>
                <tr>
                  <td>{tr("Tables :")}</td>
                  <th>{calendarInfo[availabilityDate].tables || 0}</th>
                </tr>
                <tr>
                  <td>
                    {"\uD83D\uDC68"}
                    {" : "}
                  </td>
                  <th>{calendarInfo[availabilityDate].attendees || 0}</th>
                </tr>
              </tbody>
            </table>
          )}
        </h3>

        <div style={{ margin: "20px 0px -5px", display: "ruby-text" }}>
          <h4 style={{ margin: "3px" }}>{tr("Area Filters:")}</h4>

          {isMobile ? (
            <ScrollRow className="rf-area-filters-scroll" resetKey={availabilityDate} ariaLabel="Area filters">
              {visibleFilters.map(([key, label]) => (
                <label key={key} style={{ userSelect: "none", cursor: "pointer", whiteSpace: "nowrap" }}>
                  <input
                    type="checkbox"
                    checked={!!areaFilters[key]}
                    onChange={(e) => setAreaFilters((prev) => ({ ...prev, [key]: e.target.checked }))}
                    style={{ marginRight: 6 }}
                  />
                  {tr(label)}
                </label>
              ))}
            </ScrollRow>
          ) : (
            <div style={{ marginTop: 6, display: "flex", gap: 12, flexWrap: "wrap" }}>
              {visibleFilters.map(([key, label]) => (
                <label key={key} style={{ userSelect: "none", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={!!areaFilters[key]}
                    onChange={(e) => setAreaFilters((prev) => ({ ...prev, [key]: e.target.checked }))}
                    style={{ marginRight: 6, position: "relative", top: "5px" }}
                  />
                  {tr(label)}
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Time Grid Table */}
        {loadingAvailability ? (
          <p>{tr("Loading availability...")}</p>
        ) : (
          <div className="availability-table-container" ref={containerRef}>
            <table className="availability-table">
              <thead>
                {/* First Header Row (Hours) */}
                <tr>
                  <th className="table-header">{tr("Hours")}</th>
                  {Array.from({ length: 13 }, (_, i) => (
                    <th key={i} colSpan="12" className="hour-header">
                      {12 + i}
                    </th>
                  ))}
                </tr>

                {/* Second Header Row (Minutes) */}
                <tr>
                  <th className="table-header">{tr("Minutes")}</th>
                  {Array.from({ length: 13 * 4 }, (_, i) => {
                    const minuteLabels = ["00", "15", "30", "45"];
                    return (
                      <th key={i} colSpan="3" className="minute-header">
                        {minuteLabels[i % 4]}
                      </th>
                    );
                  })}
                </tr>

                {/* Third Header Row (5-Minute Intervals - No Text) */}
                <tr>
                  <th className="table-header" style={{ color: "darkBlue" }}>
                    {tr("Tables")}
                  </th>
                  {Array.from({ length: 12 * 13 }, (_, i) => {
                    return (
                      <th
                        key={i}
                        className="micro-slot"
                        title={
                          i < 12 * 12
                            ? (12 + Math.floor(i / 12)) +
                              ":" +
                              ("0" + ((i * 5) % 60)).slice(-2) +
                              " � Available: " +
                              (visibleTablesCount -
                                (calculateSlotAvailability(i) * visibleTablesCount) / 100) +
                              " tables � " +
                              (100 - calculateSlotAvailability(i)).toFixed(1) +
                              "%"
                            : "See next day's availability"
                        }
                        style={{ background: i < 12 * 12 ? "darkGreen" : "lightGray" }}
                      >
                        {i < 12 * 12 && (
                          <div
                            className="micro-slot-availability"
                            style={{
                              height: tr("{{var1}}px", {
                                var1: calculateSlotAvailability(i) / 5,
                              }),
                            }}
                          ></div>
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>

              <tbody ref={tbodyRef}>
                {visibleTables.map((table) => (
                  <tr key={table.id} className="table-row">
                    {/* First column for table number */}
                    <td className="table-name">
                      {table.number}{" "}
                      <i style={{ fontSize: "12px", color: "black", fontWeight: "normal" }}>
                        {table.capacity}
                      </i>
                    </td>

                    {Array.from({ length: 12 * 12 }, (_, i) => {
                      const hour = 12 + Math.floor(i / 12);
                      const minute = (i % 12) * 5;

                      const startTime = tr("{{var1}}:{{var2}}", {
                        var1: String(hour).padStart(2, "0"),
                        var2: String(minute).padStart(2, "0"),
                      });

                      // Find if a reservation exists at this time slot
                      const reservationItem = table.reservations.find((res) => {
                        const resHour = +res.arrival.slice(11, 13);
                        const resMinute = +res.arrival.slice(14, 16);
                        return resHour === hour && resMinute >= minute && resMinute < minute + 5;
                      });

                      if (reservationItem) {
                        return (
                          <td
                            key={i}
                            colSpan={reservationDurationSlots}
                            className={
                              reservationItem.completed
                                ? "completed-box"
                                : reservation && reservation.id === reservationItem.id
                                ? "update-box"
                                : "reserved-box"
                            }
                            style={{ "--rf-resv-width": reservationBlockWidth }}
                          >
                            <div className="reserved-leading"></div>
                            <span className="reservation-info">
                              {table.number}) {reservationItem.arrival.slice(11, 16)} #{reservationItem.id}
                            </span>
                          </td>
                        );
                      }

                      // Ensure grid lines continue correctly after reservations
                      return (
                        <td
                          key={i}
                          className={
                            "slot " +
                            (new Date(availabilityDate).toLocaleDateString() === new Date().toLocaleDateString() &&
                            ((new Date().toLocaleTimeString("el-GR", { hour12: false }).slice(0, -6) - 12) * 12 +
                              new Date().toLocaleTimeString().slice(-8, -6) / 5) >=
                              i
                              ? "blocked"
                              : "available")
                          }
                          style={{
                            borderRight:
                              minute === 55
                                ? "2px solid darkRed"
                                : minute % 15 === 10
                                ? "1px solid black"
                                : "1px solid lightgray",
                            cursor: "pointer",
                            position: "relative",
                          }}
                          onClick={(event) => {
                            if (event.target.classList.contains("blocked")) return;

                            const time = startTime.split(":");
                            const hhmm = tr("{{var1}}:{{var2}}", {
                              var1: time[0].padStart(2, "0"),
                              var2: time[1].padStart(2, "0"),
                            });

                            setReservationDetails((prev) => ({
                              ...prev,
                              date: availabilityDate,
                              baseHours: +time[0],
                              baseMinutes: +time[1],
                              reservationTime: hhmm, // sync the time pickers
                              addedHours: 0,
                              addedMinutes: 0,
                            }));

                            onClose?.();
                            setSelectedTables([table.id]);
                          }}
                        ></td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>

            <div
              ref={hoverRef}
              className="hover-preview"
              style={{ display: "none", "--rf-hover-width": hoverBlockWidth }}
            >
              <div className="reserved-leading" style={{ marginLeft: 0 }}></div>
              <span className="hover-text"></span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AvailabilityCalendar;
